import {useEffect, useMemo, useState} from 'react';
import Select from '@/Components/Select';
import useTranslations from '@/hooks/useTranslations';

interface LanguageOption {
    value: string;
    label: string;
    url: string;
}

const LANGUAGE_NAMES: Record<string, string> = {
    ar: 'العربية',
    de: 'Deutsch',
    en: 'English',
    es: 'Español',
    fr: 'Français',
    he: 'עברית',
    id: 'Bahasa Indonesia',
    it: 'Italiano',
    km: 'ភាសាខ្មែរ',
    ko: '한국어',
    mn: 'Монгол',
    my: 'မြန်မာဘာသာ',
    pl: 'Polski',
    'pt-BR': 'Português (Brasil)',
    ro: 'Română',
    ru: 'Русский',
    th: 'ภาษาไทย',
    tr: 'Türkçe',
    vi: 'Tiếng Việt',
    zh: '中文',
    'zh-Hant': '繁體中文',
    ja: '日本語',
};

export default function LanguageSwitcher() {
    const { locale, alternateUrls } = useTranslations();
    const [languageOptions, setLanguageOptions] = useState<LanguageOption[]>([]);

    useEffect(() => {
        const options: LanguageOption[] = [];

        options.push({
            value: locale,
            label: LANGUAGE_NAMES[locale] || locale,
            url: window.location.pathname,
        });

        Object.entries(alternateUrls).forEach(([lang, url]) => {
            if (lang !== locale) {
                options.push({
                    value: lang,
                    label: LANGUAGE_NAMES[lang] || lang,
                    url,
                });
            }
        });

        setLanguageOptions(options.sort((a, b) => a.value.localeCompare(b.value)));
    }, [locale, alternateUrls]);

    const currentLanguage = languageOptions.find(option => option.value === locale);

    const handleLanguageChange = (selectedOption: LanguageOption | null) => {
        if (selectedOption && selectedOption.value !== locale) {
            window.location.href = selectedOption.url;
        }
    };

    if (languageOptions.length <= 1) {
        return null;
    }

    return (
        <div className="w-48">
            <Select<LanguageOption>
                variant="inline"
                value={currentLanguage}
                options={languageOptions}
                onChange={handleLanguageChange}
                isSearchable={true}
                placeholder="Language"
                menuPlacement="top"
                maxMenuHeight={200}
                styles={{
                    control: (base) => ({
                        ...base,
                        fontSize: '14px',
                        minHeight: '32px',
                        border: '1px solid rgb(209 213 219)',
                        '&:hover': {
                            borderColor: 'rgb(107 114 128)'
                        }
                    }),
                    singleValue: (base) => ({
                        ...base,
                        color: 'rgb(107 114 128)',
                        fontSize: '14px'
                    }),
                    placeholder: (base) => ({
                        ...base,
                        color: 'rgb(156 163 175)',
                        fontSize: '14px'
                    }),
                    menu: (base) => ({
                        ...base,
                        fontSize: '14px'
                    })
                }}
            />
        </div>
    );
}
